import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { SearchType } from 'utils/types';
import { apiService } from 'services/base.service';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ENUM_BRAND_TYPE, ENUM_SEARCH_TYPE } from 'utils/enum';
import PageText from 'components/base/page-text';

import { getSearchReponseData } from 'utils/functions';
import SearchNav from 'components/search-nav';
import PageHeaderText from 'components/base/page-header-text';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import './styles.scss';
import EditBulkModal from 'components/edit_bulk_modal';
import DeleteModal from 'components/base/delete-modal';
import MealSearchReults from './meal-search-results';
import MealCategorySearchReults from './meal-category-search-results';
import AddonSearchResults from './addon-search-results';
import BrandSearchReults from './brand-search-results';
import EditAddonModal from 'components/edit-addon-modal';
import useDashboardContext from 'contexts/dashboard.context';
import { ROUTE_PREFIX } from 'utils/constants';

const Search = () => {
  const navigate = useNavigate();
  const { activeCokitchen, trash } = useDashboardContext();
  const [searchResult, setSearchResult] = useState([] as SearchType[]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedResults, setSelectedResults] = useState([] as SearchType[]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showArchiveBulkModal, setShowArchiveBulkModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showEditAddonModal, setShowEditAddonModal] = useState(false);
  const [singleMeal, setSingleMeal] = useState<SearchType | null>(null);
  const { pathname } = useLocation();
  const { type, search_value } = useParams();
  const [showMoveBrandModal, setShowMoveBrandModal] = useState(false);
  const [activeBrandPatnership, setActiveBrandPatnership] = useState<ENUM_BRAND_TYPE | null>(null);

  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const searchMeal = async () => {
    setLoading(true);
    const payload = {
      filter_value: search_value,
      filter_type: type,
      page: String(page),
      cokitchen_id: activeCokitchen.id,
      archived: trash
    };

    try {
      const res = await apiService.post('brand/search/keyword', { data: payload });
      if (res?.data) {
        setTotalPages(res?.data?.data?.pagination?.currentPage);
        const moreData = getSearchReponseData(res?.data?.data?.data, type as ENUM_SEARCH_TYPE);
        if (page === 1) {
          setSearchResult(moreData);
        } else {
          setSearchResult([...searchResult, ...moreData]);
        }
      }
    } catch (error: any) {
      toast(<ToastMessage type='error' message={error?.response?.data?.message} />);
    }
    setLoading(false);
  };

  const toggleBulkEditModal = (refetchData?: boolean) => {
    if (refetchData) {
      searchMeal();
      setSelectedResults([]);
    }
    if (searchFilter === ENUM_SEARCH_TYPE.ADDON) {
      setShowEditAddonModal(!showEditAddonModal);
    } else if (searchFilter === ENUM_SEARCH_TYPE.BRAND) {
      navigate(`${ROUTE_PREFIX.FOOD}/brand/edit-multiple-brands`, { state: selectedResults });
    } else {
      setShowBulkEditModal(!showBulkEditModal);
    }
  };

  useEffect(() => {
    searchMeal();
  }, [page, search_value, type]);

  useEffect(() => {
    setHasMore(page < totalPages);
    if (searchResult.length === 0) {
      setSelectedResults([]);
    }
  }, [searchResult]);

  useEffect(() => {
    setPage(1);
    setSearchResult([]);
  }, [search_value, type]);

  const fetchMoreData = () => {
    setPage((p) => p + 1);
  };

  const handleChange = (result: SearchType) => {
    if (singleMeal?.id) setSingleMeal(null);
    const resultExists = selectedResults.find(({ id }) => id === result.id);

    if (resultExists) {
      setSelectedResults(selectedResults.filter(({ id }) => id !== result.id));
    } else {
      setSelectedResults([...selectedResults, result]);
    }
  };

  const singleMealClick = (result: SearchType) => {
    setSingleMeal(result);
  };

  const handleSelectAll = () => {
    setSelectedResults(searchResult);
  };

  const deleteBulk = () => {
    setShowArchiveBulkModal(!showArchiveBulkModal);
    setShowBulkDeleteModal(!showBulkDeleteModal);
  };

  const searchFilter = pathname.split('/')[3];
  const getEndpointUrl = () => {
    let url = '';
    let method: 'delete' | 'post' = 'post';
    if (searchFilter === ENUM_SEARCH_TYPE.MEAL) {
      url = 'misc/delete-multiple-meals';
    } else if (searchFilter === ENUM_SEARCH_TYPE.MEAL_CATEGORY) {
      url = 'marketing/bulk-delete-meal-category';
    } else if (searchFilter === ENUM_SEARCH_TYPE.ADDON_CATEGORY) {
      url = 'marketing/bulk-delete-meal-addon-category';
      method = 'delete';
    } else if (searchFilter === ENUM_SEARCH_TYPE.ADDON) {
      url = 'marketting/delete-addon/bulk';
    }
    return { url, method };
  };

  const getPayload = () => {
    let payload = {};
    const meal_ids = selectedResults.map((items) => items.id);
    const brand_id = selectedResults.map((item) => item.brand_id);

    if (searchFilter === ENUM_SEARCH_TYPE.MEAL) {
      payload = {
        meal_ids,
        brand_id
      };
    } else if (searchFilter === ENUM_SEARCH_TYPE.MEAL_CATEGORY) {
      payload = { meal_category_ids: meal_ids };
    } else if (searchFilter === ENUM_SEARCH_TYPE.ADDON_CATEGORY) {
      payload = { meal_addon_category_ids: meal_ids };
    } else if (searchFilter === ENUM_SEARCH_TYPE.ADDON) {
      payload = {
        addon_ids: selectedResults.map((item) => {
          return item.id;
        })
      };
    }
    return payload;
  };

  const handleDelete = async () => {
    let res;
    try {
      setLoading(true);
      if (singleMeal?.id) {
        res = await deleteSingleMeal();
      } else {
        res = await handleDeleteBulk();
      }

      if (res.data) {
        searchMeal();
        setSelectedResults([]);
        deleteBulk();
        setSingleMeal(null);
      }
    } catch {
      toast(<ToastMessage type='error' message='Error occur while archiving item' />);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBulk = async () => {
    const { url, method } = getEndpointUrl();

    // eslint-disable-next-line
    const res = await apiService[method](url, { data: getPayload() });
    if (res.data)
      toast(<ToastMessage type='success' message={`${searchFilter} succefully archived`} />);

    return res;
  };

  const deleteSingleMeal = async () => {
    const res = await apiService.delete(`marketing/meal/${singleMeal?.id}`);
    if (res) toast(<ToastMessage type='success' message='Meal archived successfully' />);

    return res;
  };
  const handleRestoreClick = async () => {
    setLoading(true);
    const itemIds =
      searchFilter === ENUM_SEARCH_TYPE.BRAND
        ? selectedResults
        : selectedResults.map(({ id }) => id);
    const payload = { type: searchFilter, item_ids: itemIds };
    try {
      const res: any = await apiService.post('marketing/restore-bulk-items', { data: payload });
      if (res.data) {
        toast(<ToastMessage type='success' message={`${searchFilter} restored successfully `} />);
        searchMeal();
        setSelectedResults([]);
        setShowRestoreModal(false);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };
  const handleArchive = () => {
    if (searchFilter === ENUM_SEARCH_TYPE.BRAND) {
      handleBrandArchive();
    } else {
      handleDelete();
    }
  };
  const handleBrandArchive = async () => {
    setLoading(true);
    try {
      const res = await apiService.post('brand_management/multiple-brand/archived', {
        data: {
          brand_ids: selectedResults,
          archived: !trash
        }
      });

      if (res?.data) {
        toast(
          <ToastMessage
            type='success'
            message={`Brand ${trash ? 'restored' : 'archived'}  successfully`}
          />
        );
        deleteBulk();
        setSelectedResults([]);
        searchMeal();
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (singleMeal?.id) {
      setSelectedResults([]);
    }
  }, [singleMeal]);

  const onMoveBrand = async () => {
    try {
      setLoading(true);
      const res: any = await apiService.post('brand-partnership/brand-partnership-move', {
        data: {
          brandIds: selectedResults.length === 0 ? [singleMeal?.id] : selectedResults,
          brand_type: activeBrandPatnership
        }
      });
      if (res?.data) {
        toast(
          <ToastMessage
            type='success'
            message={`Brands moved to ${activeBrandPatnership} successfully`}
          />
        );
        setSelectedResults([]);
        setShowMoveBrandModal(false);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='search_container'>
      <PageHeaderText title={`Search Results (${searchResult?.length})`} />
      {loading && page === 1 ? (
        <PageText text='Loading...' />
      ) : (
        <InfiniteScroll
          dataLength={searchResult.length}
          next={() => fetchMoreData()}
          hasMore={hasMore}
          loader={<h4>Loading...</h4>}
          height='100vh'
          className='noscrollbar'
          endMessage={
            <p style={{ textAlign: 'center', paddingTop: '3rem' }}>
              <b>{searchResult?.length === 0 && 'No Result For Selected Filter'}</b>
            </p>
          }>
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {searchResult
              ?.sort((a, b) => {
                const date1 = a.updated_at ?? '';
                const date2 = b.updated_at ?? '';
                return date1.localeCompare(date2);
              })
              .map((search) =>
                type === ENUM_SEARCH_TYPE.MEAL ? (
                  <MealSearchReults
                    key={search.id}
                    handleChange={handleChange}
                    search={search}
                    selectedResults={selectedResults}
                    singleMealClick={singleMealClick}
                    deleteMeal={deleteBulk}
                  />
                ) : type === ENUM_SEARCH_TYPE.MEAL_CATEGORY ||
                  type === ENUM_SEARCH_TYPE.ADDON_CATEGORY ? (
                  <MealCategorySearchReults
                    key={search.id}
                    handleChange={handleChange}
                    search={search}
                    selectedResults={selectedResults}
                    refreshData={searchMeal}
                  />
                ) : type === ENUM_SEARCH_TYPE.BRAND ? (
                  <BrandSearchReults
                    key={search.id}
                    searchMeal={searchMeal}
                    singleMealClick={singleMealClick}
                    search={search}
                    selectedResults={selectedResults}
                    setSelectedResults={setSelectedResults}
                    onMoveBrand={(type) => {
                      setActiveBrandPatnership(type);
                      setShowMoveBrandModal(true);
                    }}
                  />
                ) : type === ENUM_SEARCH_TYPE.ADDON ? (
                  <AddonSearchResults
                    key={search.id}
                    handleChange={handleChange}
                    search={search}
                    selectedResults={selectedResults}
                  />
                ) : null
              )}
          </div>

          {selectedResults.length > 0 && (
            <SearchNav
              isBrand={type === ENUM_SEARCH_TYPE.BRAND}
              count={selectedResults.length}
              handleSelectAll={handleSelectAll}
              onDeleteClick={deleteBulk}
              onRestoreClick={() => setShowRestoreModal(true)}
              onDoneClick={() => setSelectedResults([])}
              onEditClick={() => toggleBulkEditModal(false)}
              onMoveBrand={() => setShowMoveBrandModal(true)}
            />
          )}
        </InfiniteScroll>
      )}

      <EditBulkModal
        refreshData={searchMeal}
        show={showBulkEditModal}
        onClose={toggleBulkEditModal}
        data={selectedResults}
      />

      <DeleteModal
        show={showArchiveBulkModal}
        title={`Archive  ${
          singleMeal ? singleMeal?.name : `${selectedResults.length === 1 ? 'item' : 'items'}`
        }?`}
        description={`Are you sure you want to archive these items`}
        handleDelete={handleArchive}
        onClose={deleteBulk}
        loading={loading}
      />

      <DeleteModal
        show={showRestoreModal}
        title={`Restore ${
          singleMeal ? singleMeal?.name : `${selectedResults.length === 1 ? 'item' : 'items'}`
        }?`}
        description={`Are you sure you want to  restore these items`}
        handleDelete={handleRestoreClick}
        onClose={() => setShowRestoreModal(false)}
        loading={loading}
        type='restore'
      />

      <EditAddonModal
        show={showEditAddonModal}
        onClose={() => toggleBulkEditModal()}
        data={{
          name: selectedResults[0]?.name,
          addon_ids: selectedResults?.map((item) => item.id),
          amount: selectedResults[0]?.amount ?? '',
          addon_meal_id: selectedResults[0]?.addon_meal_id ?? ''
        }}
        multiple
      />
      <DeleteModal
        onClose={() => setShowMoveBrandModal(false)}
        show={showMoveBrandModal}
        description={`Are you sure you want to move ${
          selectedResults.length > 1 ? 'brands' : 'brand'
        } to ${activeBrandPatnership} `}
        handleDelete={onMoveBrand}
        loading={loading}
        title={`Move ${
          selectedResults.length > 1 ? `${selectedResults.length} brands` : 'brand'
        } to ${activeBrandPatnership} ?`}
        type='move'
      />
    </div>
  );
};

export default Search;
